import { useEffect, useState, type CSSProperties } from "react";
import { useTranslation } from "react-i18next";
import { Pawn } from "./Pawn";
import { formatDurationCompact } from "../../lib/utils/time";

export interface BoardOpenLobby {
  startTitle: string;
  targetTitle: string;
  phase: string;
  playerCount: number;
  gameRemaining: number | null;
  scoreCountdown: number | null;
}

interface BoardHeroProps {
  isDark: boolean;
  lang: string;
  /** null while no open lobby round is known. */
  openLobby: BoardOpenLobby | null;
  /** False when the open lobby is turned off or its stream is down. */
  openLobbyAvailable: boolean;
  onPlay: () => void;
  onDaily: () => void;
  onDuel?: () => void;
  onParty: () => void;
  lobbyCode: string;
  onLobbyCodeChange: (code: string) => void;
  onJoin: () => void;
}

// The board is drawn on a 1280×760 stage; everything on it is placed in
// stage coordinates and converted to percentages so the stage can scale.
const STAGE_W = 1280;
const STAGE_H = 760;
const PATH =
  "M190 620 C 330 680, 470 640, 540 540 S 470 360, 430 300 S 520 205, 650 215 S 820 280, 900 350 S 1090 340, 1110 220 S 1080 120, 1060 100";
const SPACES: [number, number, string][] = [
  [345, 655, "bg-cream"],
  [480, 620, "bg-cream"],
  [530, 480, "bg-duel"],
  [455, 350, "bg-cream"],
  [515, 240, "bg-mint"],
  [650, 215, "bg-cream"],
  [790, 268, "bg-cream"],
  [910, 350, "bg-duel"],
  [1060, 300, "bg-cream"],
  [1110, 200, "bg-mint"],
];

function at(x: number, y: number): CSSProperties {
  return {
    "--x": `${(x / STAGE_W) * 100}%`,
    "--y": `${(y / STAGE_H) * 100}%`,
  } as CSSProperties;
}

function secondsUntilNextUtcMidnight() {
  const now = new Date();
  const next = Date.UTC(
    now.getUTCFullYear(),
    now.getUTCMonth(),
    now.getUTCDate() + 1,
  );
  return (next - now.getTime()) / 1000;
}

// Its own component so the per-second tick re-renders this text, not the board.
function NextDailyCountdown() {
  const { t } = useTranslation();
  const [seconds, setSeconds] = useState(secondsUntilNextUtcMidnight);
  useEffect(() => {
    const id = setInterval(
      () => setSeconds(secondsUntilNextUtcMidnight()),
      1000,
    );
    return () => clearInterval(id);
  }, []);
  return (
    <>{t("board.nextDaily", { time: formatDurationCompact(seconds) })}</>
  );
}

/** Positioned on the desktop board, stacked on small screens. */
const ON_BOARD =
  "lg:absolute lg:left-(--x) lg:top-(--y) lg:-translate-x-1/2 lg:-translate-y-1/2";

export function BoardHero({
  isDark,
  lang,
  openLobby,
  openLobbyAvailable,
  onPlay,
  onDaily,
  onDuel,
  onParty,
  lobbyCode,
  onLobbyCodeChange,
  onJoin,
}: BoardHeroProps) {
  const { t } = useTranslation();
  const today = new Date().toLocaleDateString(lang, {
    timeZone: "UTC",
    weekday: "short",
    day: "numeric",
    month: "short",
  });

  const isRacing =
    openLobby?.phase === "Game" && (openLobby.gameRemaining ?? 1) > 0;
  const playLabel = isRacing
    ? t("board.play")
    : openLobby?.scoreCountdown != null
      ? t("board.nextRound", {
          time: formatDurationCompact(openLobby.scoreCountdown),
        })
      : openLobby || openLobbyAvailable
        ? t("board.waiting")
        : t("home.openLobby.none");

  // At night the road is a dim lane on the table rather than a cream band;
  // the cream spaces on it carry the brightness instead.
  const pathFill = isDark ? "var(--color-zinc-800)" : "var(--color-white)";
  const pathEdge = isDark ? "var(--color-edge-night)" : "var(--color-ink)";
  const tile = isDark ? "bg-cream" : "bg-white";

  return (
    <section
      aria-label={t("board.ariaLabel")}
      className="relative mx-auto w-full max-w-[1280px] px-4 pb-10 lg:aspect-[1280/760] lg:p-0"
    >
      {/* The road. Desktop only; phones get a straight dotted line. */}
      <svg
        aria-hidden="true"
        className="absolute inset-0 hidden h-full w-full lg:block"
        viewBox={`0 0 ${STAGE_W} ${STAGE_H}`}
        preserveAspectRatio="none"
      >
        <path
          d={PATH}
          fill="none"
          stroke={pathEdge}
          strokeWidth="58"
          strokeLinecap="round"
        />
        <path
          d={PATH}
          fill="none"
          stroke={pathFill}
          strokeWidth="46"
          strokeLinecap="round"
        />
      </svg>
      {SPACES.map(([x, y, color]) => (
        <span
          key={`${x}-${y}`}
          aria-hidden="true"
          style={at(x, y)}
          className={`hidden lg:flex absolute left-(--x) top-(--y) -translate-x-1/2 -translate-y-1/2 h-14 w-14 items-center justify-center rounded-full border-[3px] border-ink shadow-board font-display text-xl text-ink ${color}`}
        >
          ?
        </span>
      ))}

      <div className="flex flex-col items-center gap-6 lg:block">
        {/* Daily: a banner hanging from the top edge. */}
        <button
          type="button"
          onClick={onDaily}
          aria-label={t("home.dailyCard.title")}
          data-testid="daily-banner-btn"
          style={{ "--x": "50%" } as CSSProperties}
          className="group w-full max-w-md cursor-pointer lg:absolute lg:left-(--x) lg:top-0 lg:w-[420px] lg:-translate-x-1/2"
        >
          <span className="board-swing block bg-ink px-6 pt-5 pb-10 text-center [clip-path:polygon(0_0,100%_0,100%_100%,50%_80%,0_100%)] transition-[padding] group-hover:pb-12">
            <span className="block font-display text-5xl leading-none text-gold">
              {t("board.daily")}
            </span>
            <span className="mt-2 block font-display text-base uppercase text-cream">
              {today}
            </span>
            <span className="mt-1 block font-mono text-sm font-bold text-[#b8b2a0]">
              <NextDailyCountdown />
            </span>
          </span>
        </button>

        {/* Live count for the open lobby round. */}
        {isRacing && openLobby.playerCount > 0 && (
          <p
            className="flex items-center gap-2 rounded-xl bg-ink px-3.5 py-2 font-display text-sm text-cream lg:absolute lg:left-[3%] lg:top-[4%]"
          >
            <span className="board-pulse h-2.5 w-2.5 rounded-full bg-duel" />
            {t("board.live", { count: openLobby.playerCount })}
          </p>
        )}

        {/* Start: the open lobby's start article, with your pawn on it. */}
        <div
          style={at(190, 590)}
          className={`flex flex-col items-center gap-3 ${ON_BOARD}`}
        >
          <Pawn hopping />
          <div
            className={`-rotate-3 max-w-[280px] rounded-2xl px-6 py-2.5 text-center text-ink board-piece ${tile}`}
          >
            <div className="font-display text-xs">{t("board.start")}</div>
            <div className="line-clamp-2 font-serif text-2xl font-bold lg:text-3xl">
              {openLobby?.startTitle ?? "?"}
            </div>
          </div>
        </div>

        {/* Phones: a short dotted road between start and finish. */}
        <div aria-hidden="true" className="flex flex-col items-center gap-2 lg:hidden">
          {["bg-cream", "bg-duel", "bg-mint"].map((color) => (
            <span
              key={color}
              className={`flex h-10 w-10 items-center justify-center rounded-full border-[3px] border-ink shadow-board-sm font-display text-ink ${color}`}
            >
              ?
            </span>
          ))}
        </div>

        {/* Finish: the target, flag planted on top. */}
        <div
          style={at(1060, 92)}
          className={`flex rotate-3 flex-col items-center ${ON_BOARD}`}
        >
          <svg
            aria-hidden="true"
            width="56"
            height="64"
            viewBox="0 0 56 64"
            className="-mb-1.5 ml-10"
          >
            <path
              d="M8 62 V4"
              stroke="var(--color-ink)"
              strokeWidth="5"
              strokeLinecap="round"
            />
            <path
              d="M10 6 H48 L40 18 L48 30 H10 Z"
              fill="var(--color-duel)"
              stroke="var(--color-ink)"
              strokeWidth="4"
              strokeLinejoin="round"
            />
          </svg>
          <div className="max-w-[280px] rounded-2xl bg-gold px-6 py-3 text-center text-ink board-piece">
            <div className="font-display text-xs">{t("board.finish")}</div>
            <div className="line-clamp-2 font-serif text-2xl font-bold lg:text-3xl">
              {openLobby?.targetTitle ?? "?"}
            </div>
          </div>
        </div>

        {/* The main action: race the open lobby. */}
        <button
          type="button"
          onClick={onPlay}
          disabled={!isRacing}
          data-testid="board-play-btn"
          className="press flex h-20 w-full max-w-md cursor-pointer items-center justify-center gap-3 rounded-2xl border-4 border-ink bg-gold px-9 font-display text-2xl text-ink shadow-board-lg hover:bg-[#ffd566] disabled:cursor-not-allowed disabled:bg-cream disabled:text-lg lg:absolute lg:left-[3%] lg:top-[88%] lg:w-auto"
        >
          {isRacing && (
            <svg aria-hidden="true" width="22" height="24" viewBox="0 0 26 28">
              <path d="M3 2 L24 14 L3 26 Z" fill="currentColor" />
            </svg>
          )}
          {playLabel}
          {isRacing && openLobby.gameRemaining != null && (
            <span className="rounded-lg bg-ink px-2 py-0.5 font-mono text-base text-gold">
              {formatDurationCompact(openLobby.gameRemaining)}
            </span>
          )}
        </button>

        {/* Other ways to play: tokens in the corner. */}
        <div className="flex flex-wrap items-end justify-center gap-5 lg:absolute lg:right-[3%] lg:bottom-[3%]">
          {onDuel && (
            <BoardToken
              label={t("board.duel")}
              labelClassName={tokenLabel(isDark)}
              onClick={onDuel}
            >
              <span className="flex h-20 w-20 items-center justify-center rounded-full bg-duel font-display text-2xl text-ink board-piece">
                1v1
              </span>
            </BoardToken>
          )}
          <BoardToken
            label={t("board.party")}
            labelClassName={tokenLabel(isDark)}
            onClick={onParty}
            testId="create-lobby-btn"
          >
            <span
              className={`flex h-20 w-20 items-center justify-center gap-0.5 rounded-full board-piece ${tile}`}
            >
              <span className="h-4 w-4 rounded-full border-[3px] border-ink bg-duel" />
              <span className="h-4 w-4 rounded-full border-[3px] border-ink bg-gold" />
              <span className="h-4 w-4 rounded-full border-[3px] border-ink bg-mint" />
            </span>
          </BoardToken>
          <form
            className="flex flex-col items-center gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              onJoin();
            }}
          >
            <div
              className={`flex h-20 overflow-hidden rounded-2xl board-piece ${tile}`}
            >
              <label htmlFor="board-lobby-code" className="sr-only">
                {t("home.joinCode.placeholder")}
              </label>
              <input
                id="board-lobby-code"
                data-testid="lobby-code-input"
                value={lobbyCode}
                onChange={(e) => onLobbyCodeChange(e.target.value)}
                placeholder={t("board.codePlaceholder")}
                autoComplete="off"
                className="w-32 bg-transparent px-4 font-display text-xl tracking-wider text-ink uppercase outline-none placeholder:text-ink/40"
              />
              <button
                type="submit"
                disabled={!lobbyCode}
                data-testid="join-lobby-btn"
                className="w-16 cursor-pointer bg-ink font-display text-cream disabled:cursor-not-allowed disabled:text-cream/40"
              >
                {t("board.go")}
              </button>
            </div>
            <span className={tokenLabel(isDark)}>{t("board.join")}</span>
          </form>
        </div>
      </div>
    </section>
  );
}

function tokenLabel(isDark: boolean) {
  return `font-display text-lg ${isDark ? "text-cream [text-shadow:0_3px_0_var(--color-ink)]" : "text-ink"}`;
}

function BoardToken({
  label,
  labelClassName,
  onClick,
  testId,
  children,
}: {
  label: string;
  labelClassName: string;
  onClick: () => void;
  testId?: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className="board-token flex cursor-pointer flex-col items-center gap-2"
    >
      {children}
      <span className={labelClassName}>
        {label}
      </span>
    </button>
  );
}
